import { List, ThemeProvider } from '@sparemin/blockhead';
import { createPath } from 'history';
import * as React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useHandleEditFlow } from 'blocks/ProjectPreviewModal';
import { makeProjectNameSelector } from 'redux/modules/entities';
import { getProjectHistory } from 'redux/modules/project/actions';
import { projectHistoryHasMoreSelector } from 'redux/modules/project/selectors';
import { Dispatch } from 'redux/types';
import { ProjectTileControl } from '../types';
import {
  makeProjectClipsPathSelector,
  makeProjectRowTooltipMessageSelector,
} from '../utils';
import ProjectRow from './ProjectRow';

export interface ProjectsListProps {
  active?: boolean;
  projects: string[];
  projectRowHeaderClassName?: string;
  onControlClick?: (type: ProjectTileControl, projectId: string) => void;
}

const ProjectsList: React.FC<ProjectsListProps> = ({
  active,
  projects,
  projectRowHeaderClassName,
  onControlClick,
}) => {
  const hasMore = useSelector(projectHistoryHasMoreSelector);

  const dispatch = useDispatch<Dispatch>();

  const { startEditFlow } = useHandleEditFlow();

  const { items } = (projects ?? []).reduce(
    (acc, projectId) => {
      const projectName = dispatch((_, getState) =>
        makeProjectNameSelector(projectId)(getState()),
      );
      const tooltipMessage = dispatch((_, getState) =>
        makeProjectRowTooltipMessageSelector(projectId)(getState()),
      );
      const clipsPath = dispatch((_, getState) =>
        makeProjectClipsPathSelector(projectId)(getState()),
      );

      const item = (
        <List.Item
          key={projectId}
          textValue={projectName}
          {...(clipsPath && {
            href: createPath(clipsPath),
            target: '_blank',
          })}
        >
          <ProjectRow
            headerClassName={projectRowHeaderClassName}
            {...{ projectId, tooltipMessage, onControlClick }}
          />
        </List.Item>
      );

      return {
        items: [...acc.items, item],
      };
    },
    { items: [] },
  );

  const handleLoadMore = React.useCallback(() => {
    if (active) {
      dispatch(getProjectHistory(true));
    }
  }, [active, dispatch]);

  const handleAction = React.useCallback(
    async (projectId: string): Promise<void> => {
      const clipsPath = dispatch((_, getState) =>
        makeProjectClipsPathSelector(projectId)(getState()),
      );

      if (clipsPath) {
        return undefined;
      }

      const action = await startEditFlow(projectId);

      if (typeof action === 'string') {
        onControlClick?.(action, projectId);
      }

      return undefined;
    },
    [dispatch, onControlClick, startEditFlow],
  );

  return (
    <ThemeProvider theme="dark">
      <List
        aria-label="projects list"
        // While the 'projects' list is undefined, set 'hasMore' to true to
        // show the list in a loading state.
        hasMore={projects === undefined ? true : hasMore && active}
        onLoadMore={handleLoadMore}
        onAction={handleAction}
      >
        {items}
      </List>
    </ThemeProvider>
  );
};

export default ProjectsList;
